import { useQuery } from '@tanstack/vue-query'
import type {
  QueryParamsByPath,
  ResponseByPath,
  SchemaAttributes,
  Schemas,
} from '@@/api/types.js'
import { useApi } from '@/composables/useApi.js'

const SPECIES_URL = '/{provet_id}/api/species'

export type SpeciesQueryParams = QueryParamsByPath<'get', typeof SPECIES_URL>
export type SpeciesListResponse = ResponseByPath<'get', typeof SPECIES_URL>
export type Species = Schemas['Species']
export type SpeciesAttributes = SchemaAttributes<'Species'>

type SpeciesCacheOptions = {
  staleTime?: number
  refetchOnWindowFocus?: boolean
  refetchOnReconnect?: boolean
  refetchOnMount?: boolean
}

const speciesKeys = {
  all: ['species'] as const,
  lists: () => [...speciesKeys.all, 'list'] as const,
  list: (query: MaybeRefOrGetter<SpeciesQueryParams>) =>
    [...speciesKeys.lists(), query] as const,
}

export const useSpecies = (cacheOptions?: SpeciesCacheOptions) => {
  const api = useApi()
  const provetId = useRouteParams<string>('provetId')

  const query = useQuery({
    queryKey: speciesKeys.all,
    queryFn: async () => {
      return api((client) =>
        client.GET(SPECIES_URL, {
          params: {
            path: { provet_id: provetId.value },
          },
        }),
      )
    },
    ...(cacheOptions ?? {}),
  })

  return reactive(query)
}

export const SPECIES_OPTIONS_CACHE_OPTIONS = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
} as const

export function mapSpeciesToOptions(
  data: SpeciesListResponse['data'] | undefined,
): Array<{ value: string; label: string }> {
  if (!data) {
    return []
  }
  return data.map((species) => ({
    value: species.id,
    label: species.attributes?.name ?? '',
  }))
}
